import { parseCivilDate } from '@klopotun/domain';
import { z } from 'zod';
export const SMALLINT_MAX = 32767;
export const uuidSchema = z.uuid();
export const currencyCodeSchema = z
    .string()
    .regex(/^[A-Z]{3}$/, 'must be a three letter ISO 4217 code');
const isRealCalendarDate = (value) => {
    try {
        // Postgres has no year zero: its `date` runs from 4713 BC, written as BC, and refuses
        // "0000-01-01" as out of range. The calendar rule in packages/domain knows nothing about the
        // bounds of a column, so the lower bound belongs here beside the reason for the check.
        return parseCivilDate(value).year >= 1;
    }
    catch {
        return false;
    }
};
/**
 * The shape alone is not enough: a `date` column refuses "2026-02-30" with a cast error, which is
 * no HttpException and would reach the client as a 500. The calendar rule itself stays in
 * packages/domain - a second copy of the leap year rule here would drift away from it.
 */
export const civilDateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'must be a calendar date in YYYY-MM-DD form')
    .refine(isRealCalendarDate, 'that day does not exist in that month');
export const isoTimestampSchema = z.iso.datetime();
/**
 * A cleared text input sends "", never null, and the mini app has no mapper layer to turn one into
 * the other (design note C11). Keeping both "" and NULL in a column would force every reader to
 * test for two kinds of empty, so the empty string is normalised away right here.
 */
export const optionalTextSchema = z
    .string()
    .nullable()
    .transform((value) => (value === '' ? null : value));
/**
 * z.int() already refuses anything past Number.MAX_SAFE_INTEGER, which is exactly the upper bound
 * the amount_minor CHECK enforces in the database - a second .max() here would only duplicate the
 * error message.
 */
export const amountMinorSchema = z.int().positive();
/**
 * The one amount here that may honestly be zero, and the only reason it is a schema of its own:
 * converting a payment worth less than half a minor unit of the base currency rounds to nothing,
 * and refusing that would fail the whole confirmation over a rounding rule. It mirrors
 * occurrence_base_amount_ck, which migration 0003 relaxed to `>= 0` for exactly this case - the
 * database is the source of truth and this schema follows it field for field.
 */
export const convertedAmountMinorSchema = z.int().min(0);
/**
 * Room for the default overdue ladder of design note F10 - every day up to the thirtieth - and one
 * more, so a ladder that also names the due date itself is still a valid one.
 */
const MAX_REMINDER_STEPS = 31;
export const reminderStepDaysSchema = z
    .array(z.int().min(0).max(SMALLINT_MAX))
    .max(MAX_REMINDER_STEPS);
/**
 * Query strings carry booleans as text, and z.coerce.boolean() turns the string "false" into
 * true - every non-empty string is truthy. Only an explicit pair of words is safe here.
 */
export const booleanQueryParamSchema = z
    .enum(['true', 'false'])
    .transform((value) => value === 'true');
//# sourceMappingURL=common.js.map